import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { useSharedData } from './SharedDataContext';

export interface Region {
  name: string;        // max 30 characters
  color: string;      // one of: white, red, orange, yellow, green, blue, purple
  startTime: number;  // in seconds
  endTime: number | null; // null = open region (extends to end of track)
}

export interface RegionsContextType {
  getRegions: (fileId: string) => Region[];
  addRegion: (fileId: string, region: Region) => void;
  updateRegion: (fileId: string, startTime: number, updates: Partial<Region>) => void;
  endOpenRegion: (fileId: string, endTime: number) => void;
  removeRegion: (fileId: string, startTime: number) => void;
  getOpenRegion: (fileId: string) => Region | null;
  getRegionByStartTime: (fileId: string, startTime: number) => Region | null;
  migrateRegionsFromTrackNumbers: (files: Array<{ id: string }>) => void;
  regions: Record<string, Region[]>;
}

const RegionsContext = createContext<RegionsContextType | undefined>(undefined);

// Create a persistent object to store regions by file ID
const persistentRegions: Record<string, Region[]> = {};

export const RegionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use ref to maintain the same object reference
  const regionsRef = useRef(persistentRegions);
  const [, forceUpdate] = useState({});
  const { shareData } = useSharedData();

  // Load regions from shared data when available
  // Shared data has regions keyed by track position ("1", "2", "3")
  // which matches track.id. We'll store them with track- prefix for migration.
  useEffect(() => {
    if (shareData?.regions) {
      console.log('[Regions] Loading regions from shareData:', shareData.regions);
      // Clear existing regions before loading new ones
      Object.keys(regionsRef.current).forEach(key => {
        delete regionsRef.current[key];
      });
      
      // Store shared regions
      // Format: keys are track positions ("1", "2", "3") matching track.id
      Object.entries(shareData.regions).forEach(([key, value]) => {
        // Check if key is a simple number (track position format)
        if (/^\d+$/.test(key)) {
          // Store with track- prefix - will be migrated when files load
          regionsRef.current[`track-${key}`] = value as Region[];
          console.log(`[Regions] Stored track-${key}:`, value);
        }
      });
      
      console.log('[Regions] All stored regions:', Object.keys(regionsRef.current));
      forceUpdate({}); // Force a re-render
    }
  }, [shareData]);

  const getRegions = (fileId: string): Region[] => {
    return regionsRef.current[fileId] || [];
  };

  const addRegion = (fileId: string, region: Region) => {
    if (!regionsRef.current[fileId]) {
      regionsRef.current[fileId] = [];
    }
    regionsRef.current[fileId].push(region);
    // Sort regions by startTime
    regionsRef.current[fileId].sort((a, b) => a.startTime - b.startTime);
    forceUpdate({}); // Force a re-render
  };

  const updateRegion = (fileId: string, startTime: number, updates: Partial<Region>) => {
    const regions = regionsRef.current[fileId];
    if (!regions) return;
    
    const index = regions.findIndex(r => r.startTime === startTime);
    if (index !== -1) {
      regions[index] = { ...regions[index], ...updates };
      forceUpdate({}); // Force a re-render
    }
  };

  const endOpenRegion = (fileId: string, endTime: number) => {
    const regions = regionsRef.current[fileId];
    if (!regions) return;
    
    // Find the open region (endTime === null)
    const openRegionIndex = regions.findIndex(r => r.endTime === null);
    if (openRegionIndex !== -1) {
      regions[openRegionIndex].endTime = endTime;
      // Sort regions by startTime
      regions.sort((a, b) => a.startTime - b.startTime);
      forceUpdate({}); // Force a re-render
    }
  };

  const removeRegion = (fileId: string, startTime: number) => {
    const regions = regionsRef.current[fileId];
    if (!regions) return;
    
    const index = regions.findIndex(r => r.startTime === startTime);
    if (index !== -1) {
      regions.splice(index, 1);
      if (regions.length === 0) {
        delete regionsRef.current[fileId];
      }
      forceUpdate({}); // Force a re-render
    }
  };

  const getOpenRegion = (fileId: string): Region | null => {
    const regions = regionsRef.current[fileId];
    if (!regions) return null;
    
    return regions.find(r => r.endTime === null) || null;
  };

  const getRegionByStartTime = (fileId: string, startTime: number): Region | null => {
    const regions = regionsRef.current[fileId];
    if (!regions) return null;
    
    return regions.find(r => r.startTime === startTime) || null;
  };

  // Migrate regions from track numbers (legacy) or shared data to file IDs
  const migrateRegionsFromTrackNumbers = (files: Array<{ id: string }>) => {
    const migrated = new Set<string>();
    
    console.log('[Regions] Starting migration for', files.length, 'files');
    console.log('[Regions] Available regions:', Object.keys(regionsRef.current));
    
    files.forEach((file, index) => {
      // Extract original track.id from file.id
      // File IDs can be:
      // - "shared-{track.id}" (selective loading)
      // - "{track.id}" (normal loading, track.id as string)
      // - Other formats (legacy)
      let trackId: string | null = null;
      
      // Try to extract from "shared-{id}" format (selective loading)
      if (file.id.startsWith('shared-')) {
        trackId = file.id.replace('shared-', '');
        console.log(`[Regions] File ${file.id}: extracted trackId=${trackId}`);
      } else if (/^\d+$/.test(file.id)) {
        // File ID is already just the track.id as a number string
        trackId = file.id;
        console.log(`[Regions] File ${file.id}: trackId=${trackId} (direct)`);
      }
      
      // First, try track number format using the extracted track.id
      if (trackId) {
        const trackKey = `track-${trackId}`;
        const trackRegions = regionsRef.current[trackKey];
        console.log(`[Regions] Looking for ${trackKey}:`, trackRegions ? 'FOUND' : 'NOT FOUND');
        if (trackRegions) {
          regionsRef.current[file.id] = trackRegions;
          delete regionsRef.current[trackKey];
          migrated.add(trackKey);
          console.log(`[Regions] Migrated ${trackKey} -> ${file.id}:`, trackRegions);
          return; // Found and migrated, move to next file
        }
      }
      
      // Fallback: try track number format using index+1 (for legacy cases)
      const trackNumber = index + 1;
      const trackKey = `track-${trackNumber}`;
      const trackRegions = regionsRef.current[trackKey];
      if (trackRegions) {
        regionsRef.current[file.id] = trackRegions;
        delete regionsRef.current[trackKey];
        migrated.add(trackKey);
        return; // Found and migrated, move to next file
      }
    });
    
    // Clean up any remaining legacy track- keys that weren't migrated
    Object.keys(regionsRef.current).forEach(key => {
      // Only clean up track-* keys that weren't migrated
      if (key.startsWith('track-') && !migrated.has(key)) {
        delete regionsRef.current[key];
      }
    });
    
    if (migrated.size > 0) {
      console.log('[Regions] Migration complete. Final regions:', Object.keys(regionsRef.current));
      forceUpdate({}); // Force a re-render
    }
  };

  const value: RegionsContextType = {
    getRegions,
    addRegion,
    updateRegion,
    endOpenRegion,
    removeRegion,
    getOpenRegion,
    getRegionByStartTime,
    migrateRegionsFromTrackNumbers,
    regions: regionsRef.current,
  };

  return (
    <RegionsContext.Provider value={value}>
      {children}
    </RegionsContext.Provider>
  );
};

export const useRegions = (): RegionsContextType => {
  const context = useContext(RegionsContext);
  if (!context) {
    throw new Error('useRegions must be used within a RegionsProvider');
  }
  return context;
};

